import { useEffect } from "react";

const DEFAULT_OG_IMAGE = "https://www.planamoto.com/og-image.png";
const SITE_ORIGIN = "https://www.planamoto.com";

const upsertMeta = (key, content, attr = "name") => {
    const selector = `meta[${attr}="${key}"]`;
    let el = document.head.querySelector(selector);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
};

const readMeta = (key, attr = "name") => {
    const el = document.head.querySelector(`meta[${attr}="${key}"]`);
    return el ? el.getAttribute("content") : null;
};

const removeMeta = (key, attr = "name") => {
    const el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (el) el.remove();
};

const upsertLink = (rel, href) => {
    const selector = `link[rel="${rel}"]`;
    let el = document.head.querySelector(selector);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
};

const readLink = (rel) => {
    const el = document.head.querySelector(`link[rel="${rel}"]`);
    return el ? el.getAttribute("href") : null;
};

const removeLink = (rel) => {
    const el = document.head.querySelector(`link[rel="${rel}"]`);
    if (el) el.remove();
};

const buildCanonicalUrl = (path) => {
    if (typeof path === "string" && path.trim()) {
        return path.startsWith("http") ? path : `${SITE_ORIGIN}${path}`;
    }
    if (typeof window !== "undefined" && window.location) {
        return `${SITE_ORIGIN}${window.location.pathname}`;
    }
    return SITE_ORIGIN;
};

/**
 * @param {{ title: string, description?: string, image?: string, url?: string, noindex?: boolean }} props
 */
export default function usePageMeta({ title, description, image, url, noindex }) {
    useEffect(() => {
        const prevTitle = document.title;
        const prevDescription = readMeta("description");
        const prevOgImage = readMeta("og:image", "property");
        const prevTwImage = readMeta("twitter:image");
        const prevOgUrl = readMeta("og:url", "property");
        const prevCanonical = readLink("canonical");

        document.title = title;

        // Update OG/Twitter title to match document title.
        upsertMeta("og:title", title, "property");
        upsertMeta("twitter:title", title);

        const canonicalUrl = buildCanonicalUrl(url);
        upsertLink("canonical", canonicalUrl);
        upsertMeta("og:url", canonicalUrl, "property");

        if (description) {
            upsertMeta("description", description);
            upsertMeta("og:description", description, "property");
            upsertMeta("twitter:description", description);
        }

        if (image) {
            upsertMeta("og:image", image, "property");
            upsertMeta("twitter:image", image);
        }

        if (noindex) {
            upsertMeta("robots", "noindex,nofollow");
        }

        return () => {
            document.title = prevTitle;
            // Restore description so the next page (which may not call usePageMeta) doesn't inherit stale text.
            if (description && prevDescription) {
                upsertMeta("description", prevDescription);
                upsertMeta("og:description", prevDescription, "property");
                upsertMeta("twitter:description", prevDescription);
            }
            // Restore OG/Twitter image to whatever was there before (site default from index.html).
            if (image) {
                upsertMeta("og:image", prevOgImage || DEFAULT_OG_IMAGE, "property");
                upsertMeta("twitter:image", prevTwImage || DEFAULT_OG_IMAGE);
            }
            // Restore canonical + og:url to whatever index.html had (or remove if there was none).
            if (prevCanonical) upsertLink("canonical", prevCanonical);
            else removeLink("canonical");
            if (prevOgUrl) upsertMeta("og:url", prevOgUrl, "property");
            if (noindex) removeMeta("robots");
        };
    }, [title, description, image, url, noindex]);
}
